'use client';

import React, { useState } from 'react';
import { ClipboardIcon, CheckCircleIcon, ThermometerIcon, FlameIcon } from '../../Icons';

const GAS_MARKS: { [key: number]: { minC: number, maxC: number } } = {
    '0.5': { minC: 110, maxC: 129 },
    1: { minC: 130, maxC: 139 }, 2: { minC: 140, maxC: 149 }, 3: { minC: 150, maxC: 169 },
    4: { minC: 170, maxC: 179 }, 5: { minC: 180, maxC: 199 }, 6: { minC: 200, maxC: 209 },
    7: { minC: 210, maxC: 229 }, 8: { minC: 230, maxC: 239 }, 9: { minC: 240, maxC: 250 },
};

const getGasMark = (celsius: number): string => {
    if (celsius < 110) return 'Low';
    for (const mark in GAS_MARKS) {
        if (celsius >= GAS_MARKS[mark].minC && celsius <= GAS_MARKS[mark].maxC) {
            return mark;
        }
    }
    if (celsius < 135) return '1/2';
    if (celsius < 145) return '1';
    if (celsius < 155) return '2';
    if (celsius < 175) return '3';
    if (celsius < 185) return '4';
    if (celsius < 195) return '5';
    if (celsius < 215) return '6';
    if (celsius < 225) return '7';
    if (celsius < 235) return '8';
    return '9+';
};

const InputField: React.FC<{
    label: string;
    value: string;
    onChange: (val: string) => void;
    icon?: React.ReactNode;
    active?: boolean;
    readOnly?: boolean;
}> = ({ label, value, onChange, icon, active, readOnly }) => (
    <div className={`relative group transition-all duration-300 ${active ? 'scale-105 z-10' : 'opacity-90 hover:opacity-100'}`}>
        <label className={`block text-xs font-bold uppercase tracking-wide mb-2 transition-colors ${active ? 'text-brand-pink' : 'text-brand-text-body/70'}`}>{label}</label>
        <div className="relative shadow-sm rounded-xl overflow-hidden">
            <input
                type={readOnly ? "text" : "number"}
                value={value}
                onChange={e => !readOnly && onChange(e.target.value)}
                readOnly={readOnly}
                className={`w-full pl-12 pr-4 py-4 text-3xl font-bold border-2 rounded-xl transition-all outline-none ${active ? 'border-brand-pink bg-white ring-4 ring-brand-pink/10' : readOnly ? 'border-brand-border bg-slate-50' : 'border-brand-border bg-brand-bg/20 hover:border-brand-pink/50'}`}
                placeholder="0"
            />
            <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${active ? 'text-brand-pink' : 'text-brand-text-body/40'}`}>
                {icon}
            </div>
        </div>
    </div>
);

export const TemperatureConverterClient: React.FC = () => {
    const [celsius, setCelsius] = useState('180');
    const [fahrenheit, setFahrenheit] = useState('350');
    const [kelvin, setKelvin] = useState('453.15');
    const [gasMark, setGasMark] = useState('4');
    const [fanCelsius, setFanCelsius] = useState('160');
    const [activeUnit, setActiveUnit] = useState<'C' | 'F' | 'K' | 'G' | 'Fan'>('C');
    const [copied, setCopied] = useState(false);

    const updateValues = (c: number) => {
        if (isNaN(c)) {
            setCelsius(''); setFahrenheit(''); setKelvin(''); setGasMark(''); setFanCelsius('');
            return;
        }
        setCelsius(Math.round(c).toString());
        setFahrenheit(Math.round((c * 9 / 5) + 32).toString());
        setKelvin((c + 273.15).toFixed(2).replace(/\.00$/, ''));
        setGasMark(getGasMark(c));
        setFanCelsius(Math.round(c - 20).toString());
    };

    const handleCelsiusChange = (val: string) => {
        setCelsius(val);
        setActiveUnit('C');
        const c = parseFloat(val);
        if (!isNaN(c)) updateValues(c);
    };

    const handleFahrenheitChange = (val: string) => {
        setFahrenheit(val);
        setActiveUnit('F');
        const f = parseFloat(val);
        if (!isNaN(f)) {
            const c = (f - 32) * 5 / 9;
            updateValues(c);
        }
    };

    const handleKelvinChange = (val: string) => {
        setKelvin(val);
        setActiveUnit('K');
        const k = parseFloat(val);
        if (!isNaN(k)) {
            const c = k - 273.15;
            updateValues(c);
        }
    };

    const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const c = parseFloat(e.target.value);
        setActiveUnit('C');
        updateValues(c);
    };

    const handleCopy = async () => {
        const text = `${celsius}°C = ${fahrenheit}°F = Gas Mark ${gasMark} = ${fanCelsius}°C (Fan)`;
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {}
    };

    return (
        <div className="grid lg:grid-cols-12 gap-8 items-start text-brand-text-body">
            <div className="lg:col-span-7 space-y-8">
                <div className="bg-white p-6 md:p-8 rounded-2xl border border-brand-border shadow-lg relative overflow-hidden">
                    <div className="relative flex flex-col gap-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputField label="Celsius (°C)" value={celsius} onChange={handleCelsiusChange} icon={<span className="text-xl font-serif font-bold">°C</span>} active={activeUnit === 'C'} />
                            <InputField label="Fahrenheit (°F)" value={fahrenheit} onChange={handleFahrenheitChange} icon={<span className="text-xl font-serif font-bold">°F</span>} active={activeUnit === 'F'} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputField label="Gas Mark" value={gasMark} onChange={() => {}} icon={<FlameIcon className="w-6 h-6" />} readOnly />
                            <InputField label="Fan Oven (°C)" value={fanCelsius} onChange={() => {}} icon={<span className="text-xl font-serif font-bold">Fan</span>} readOnly />
                        </div>
                        <InputField label="Kelvin (K)" value={kelvin} onChange={handleKelvinChange} icon={<span className="text-xl font-serif font-bold">K</span>} active={activeUnit === 'K'} />
                        
                        <div className="pt-4 px-2">
                            <label className="block text-xs font-bold uppercase tracking-wide text-brand-text-body/50 mb-4 text-center">Quick Adjust Slider (Baking Range)</label>
                            <input type="range" min="50" max="260" value={parseFloat(celsius) || 180} onChange={handleSliderChange} className="w-full h-2 bg-brand-bg rounded-lg appearance-none cursor-pointer accent-brand-pink" />
                            <div className="flex justify-between text-xs text-brand-text-body/40 mt-2 font-medium">
                                <span>Warm (50°C)</span>
                                <span>Baking (180°C)</span>
                                <span>Hot (260°C)</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="text-center">
                    <button onClick={handleCopy} className="inline-flex items-center gap-2 px-8 py-4 bg-white border-2 border-brand-border rounded-full text-brand-pink font-bold hover:bg-brand-bg hover:border-brand-pink transition-all shadow-sm">
                        {copied ? <CheckCircleIcon className="w-5 h-5" /> : <ClipboardIcon className="w-5 h-5" />}
                        {copied ? 'Copied!' : 'Copy All Results'}
                    </button>
                </div>
            </div>
            <div className="lg:col-span-5 space-y-6">
                <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm">
                    <h3 className="font-serif font-bold text-xl mb-6 text-brand-text-title flex items-center gap-2"><ThermometerIcon className="w-6 h-6 text-brand-pink" />Common Baking Temps</h3>
                    <div className="space-y-3">
                        {[
                            { c: '150°C', f: '300°F', d: 'Slow (Gas 2)' },
                            { c: '180°C', f: '350°F', d: 'Moderate (Gas 4)' },
                            { c: '200°C', f: '400°F', d: 'Hot (Gas 6)' },
                            { c: '220°C', f: '425°F', d: 'Very Hot (Gas 7)' }
                        ].map((item, i) => (
                            <div key={i} className="p-4 bg-brand-bg/20 rounded-xl hover:bg-brand-pink/5 transition-colors cursor-pointer" onClick={() => handleCelsiusChange(item.c.replace('°C', ''))}>
                                <div className="flex justify-between font-bold text-brand-text-title text-lg"><span>{item.c}</span><span className="text-brand-text-body/40">|</span><span>{item.f}</span></div>
                                <p className="text-xs text-brand-text-body">{item.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
